import api from '../utils/api';

const exportService = {
  /**
   * Export files/folders as ZIP
   * @param {string} dataroomUuid - The dataroom UUID
   * @param {array} uuids - Optional array of file/folder UUIDs to export. If not provided, exports all files
   * @returns {Promise} - Returns a blob response for download
   */
  exportFilesAsZip: async (dataroomUuid, uuids = null) => {
    try {
      const config = {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        }
      };

      const body = {};

      // Only include uuids in body if provided
      if (uuids && uuids.length > 0) {
        body.uuids = Array.isArray(uuids) ? uuids : uuids.split(',').map(uuid => uuid.trim());
      }

      const response = await api.post(
        `/datarooms/${dataroomUuid}/files/export-zip`,
        body,
        config
      );

      return response;
    } catch (error) {
      console.error('Error exporting files as ZIP:', error);
      throw error;
    }
  },

  /**
   * Export files by scope (recently_uploaded, recently_opened, favorites)
   * @param {string} dataroomUuid - The dataroom UUID
   * @param {string} scope - The scope to export (recently_uploaded|recently_opened|favorites)
   * @returns {Promise}
   */
  exportFilesByScope: async (dataroomUuid, scope) => {
    try {
      const response = await api.get(
        `/datarooms/${dataroomUuid}/files/export`,
        {
          params: { scope },
          responseType: 'blob'
        }
      );

      return response;
    } catch (error) {
      console.error('Error exporting files by scope:', error);
      throw error;
    }
  },

  /**
   * Trigger download of blob data
   * @param {Blob} blob - The blob data to download
   * @param {string} filename - The filename for the download
   */
  downloadBlob: (blob, filename = 'export.zip') => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Extract filename from Content-Disposition header
   * @param {string} contentDisposition - The Content-Disposition header value
   * @returns {string|null} - The extracted filename or null if not found
   */
  extractFilenameFromContentDisposition: (contentDisposition) => {
    if (!contentDisposition) return null;

    // Try different patterns to extract filename
    let filename = null;

    // Pattern 1: filename*=UTF-8''... (RFC 5987)
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) {
      try {
        filename = decodeURIComponent(utf8Match[1]);
        return filename;
      } catch (e) {
        // Continue to next pattern
      }
    }

    // Pattern 2: filename="..." (with quotes)
    const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (quotedMatch) {
      filename = quotedMatch[1];
      try {
        // Try to decode in case it's URL encoded
        filename = decodeURIComponent(filename);
      } catch (e) {
        // Use as is if decoding fails
      }
      return filename;
    }

    // Pattern 3: filename=... (without quotes)
    const unquotedMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (unquotedMatch) {
      filename = unquotedMatch[1].trim();
      // Remove quotes if present
      if ((filename.startsWith('"') && filename.endsWith('"')) ||
          (filename.startsWith("'") && filename.endsWith("'"))) {
        filename = filename.slice(1, -1);
      }
      try {
        // Try to decode in case it's URL encoded
        filename = decodeURIComponent(filename);
      } catch (e) {
        // Use as is if decoding fails
      }
      return filename;
    }

    return null;
  }
};

export default exportService;